/**
 * @fileoverview UC-REG-301 — Recetas y órdenes posdatadas (fecha de emisión
 *   diferida). Lógica fuera de `Indications.vue` (no se monta bajo vue-jest):
 *   campo "Fecha de emisión" que se agrega a los formularios de los tipos
 *   que lo admiten cuando el tenant habilita un máximo de días
 *   (`VUE_APP_INDICATION_POSTDATED_MAX_DAYS`), opciones del date picker y
 *   texto de ayuda. La API valida lo mismo (`INDICATIONS_POSTDATED_MAX_DAYS`).
 */

/** @description Tipos que admiten fecha de emisión diferida (igual que la API). */
export const POSTDATED_TYPES = ["medications", "lab", "images", "practices"];

/** @description Clave del campo en `defaultValues` / body del POST. */
export const ISSUED_AT_KEY = "issuedAt";

/**
 * @description Definición del campo "Fecha de emisión" para `defaultValues`.
 * @returns {Object}
 */
export function issuedAtField() {
  return {
    friendlyName: "Fecha de emisión",
    value: "",
    inputType: "date",
    placeholder: "Hoy",
    required: false,
  };
}

/**
 * @description Agrega el campo de fecha de emisión a los formularios de los
 *   tipos que lo admiten, solo si `maxDays > 0`. No muta los originales.
 * @param {Array<Object>} forms - Entradas de `INDICATIONS_TYPES`
 * @param {number} maxDays
 * @returns {Array<Object>}
 */
export function withIssuedAtField(forms, maxDays) {
  if (!(Number(maxDays) > 0)) return forms;
  return (forms || []).map((form) => {
    if (!form || !POSTDATED_TYPES.includes(form.type) || !form.defaultValues) {
      return form;
    }
    return {
      ...form,
      defaultValues: {
        ...form.defaultValues,
        [ISSUED_AT_KEY]: issuedAtField(),
      },
    };
  });
}

/**
 * @description Medianoche local de una fecha.
 * @param {Date} d
 * @returns {Date}
 */
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * @description Opciones del `el-date-picker`: deshabilita días anteriores a
 *   hoy y posteriores a hoy + maxDays.
 * @param {number} maxDays
 * @param {Date} [now]
 * @returns {{disabledDate: function(Date): boolean}}
 */
export function issuedAtPickerOptions(maxDays, now = new Date()) {
  const min = startOfDay(now);
  const max = new Date(
    min.getTime() + Math.max(0, Number(maxDays) || 0) * 86400000
  );
  return {
    disabledDate(date) {
      const d = startOfDay(date);
      return d < min || d > max;
    },
  };
}

/**
 * @description "yyyy-MM-dd" dentro de [hoy, hoy + maxDays].
 * @param {string} value
 * @param {number} maxDays
 * @param {Date} [now]
 * @returns {boolean} true también si está vacío (opcional)
 */
export function isIssuedAtInRange(value, maxDays, now = new Date()) {
  if (!value) return true;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!m) return false;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return !issuedAtPickerOptions(maxDays, now).disabledDate(d);
}

/**
 * @description Ayuda bajo el campo.
 * @param {number} maxDays
 * @returns {string} '' si las posdatadas están deshabilitadas
 */
export function issuedAtHint(maxDays) {
  const n = Number(maxDays) || 0;
  if (n <= 0) return "";
  return `Opcional: podés diferir la emisión hasta ${n} día${
    n === 1 ? "" : "s"
  }. Vacío = hoy.`;
}
